import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Trophy, Users, Calendar as CalendarIcon, ExternalLink, Mail, Award, BookOpen } from 'lucide-react'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment-timezone'
import axios from 'axios'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import CourtBookingForm from '../components/CourtBookingForm'

// Set default timezone to Eastern Time
const TIMEZONE = 'America/New_York'
moment.tz.setDefault(TIMEZONE)
const localizer = momentLocalizer(moment)

const Tennis = () => {
  const [bookings, setBookings] = useState([])
  const [events, setEvents] = useState([])
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [showBookingForm, setShowBookingForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [currentView, setCurrentView] = useState('day')
  const [selectedEvent, setSelectedEvent] = useState(null)

  const courts = [
    { value: 'all', label: 'All Courts' },
    { value: 'court_1_lower_deck', label: 'Court 1 - Lower Deck Side' },
    { value: 'court_2_lower_creek', label: 'Court 2 - Lower Creek Side' },
    { value: 'court_3_upper_pavilion', label: 'Court 3 - Upper Pavilion Side' },
    { value: 'court_4_upper_creek', label: 'Court 4 - Upper Creek Side' }
  ]

  // Calendar resources for column view
  const calendarResources = [
    {
      resourceId: 'court_1_lower_deck',
      resourceTitle: 'Court 1\nLower Deck'
    },
    {
      resourceId: 'court_2_lower_creek',
      resourceTitle: 'Court 2\nLower Creek'
    },
    {
      resourceId: 'court_3_upper_pavilion',
      resourceTitle: 'Court 3\nUpper Pavilion'
    },
    {
      resourceId: 'court_4_upper_creek',
      resourceTitle: 'Court 4\nUpper Creek'
    }
  ]

  // Abbreviate name to first name + last initial for privacy
  const abbreviateName = (fullName) => {
    const parts = fullName.trim().split(/\s+/)
    if (parts.length < 2) return fullName
    return `${parts[0]} ${parts[parts.length - 1][0]}.`
  }

  // Fetch court bookings from the API
  const fetchBookings = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get('/api/bookings')

      // Show both approved and pending court bookings with valid data
      const courtBookings = response.data.filter(
        booking => booking.facility?.startsWith('court_') &&
        (booking.status === 'approved' || booking.status === 'pending') &&
        booking.startDate && booking.startTime && booking.endTime
      )

      // Transform bookings into calendar events (in Eastern Time)
      const calendarEvents = courtBookings.map(booking => {
        // For court bookings, always use the startDate for both start and end times
        // since bookings are same-day (endDate can be next day in UTC due to timezone conversion)
        const dateStr = typeof booking.startDate === 'string' ? booking.startDate : new Date(booking.startDate).toISOString()
        const bookingDate = dateStr.split('T')[0]
        const startDateTime = moment.tz(`${bookingDate} ${booking.startTime}`, 'YYYY-MM-DD HH:mm', TIMEZONE).toDate()
        const endDateTime = moment.tz(`${bookingDate} ${booking.endTime}`, 'YYYY-MM-DD HH:mm', TIMEZONE).toDate()

        // Get court label
        const courtLabel = courts.find(c => c.value === booking.facility)?.label || booking.facility
        const courtNumber = courtLabel.match(/Court (\d)/)?.[1] || ''

        const statusLabel = booking.status === 'pending' ? ' (Pending)' : ''
        const reasonLabel = booking.eventType ? ` - ${booking.eventType}` : ''

        return {
          id: booking._id,
          title: `Court ${courtNumber}: ${abbreviateName(booking.contactName)}${reasonLabel}${statusLabel}`,
          start: startDateTime,
          end: endDateTime,
          resourceId: booking.facility, // Map to court column
          resource: {
            facility: booking.facility,
            contactName: booking.contactName,
            eventType: booking.eventType,
            status: booking.status,
            courtLabel: courtLabel
          }
        }
      }).filter(event => !isNaN(event.start.getTime()) && !isNaN(event.end.getTime()))

      setBookings(courtBookings)
      setEvents(calendarEvents)
    } catch (err) {
      console.error('Error fetching court bookings:', err)
      setBookings([])
      setEvents([])
      setError(null) // Don't show error to allow users to still make reservations
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBookings()
  }, [])

  // No filtering needed since we show all courts in columns
  const filteredEvents = events

  // Handle slot selection (clicking on calendar)
  const handleSelectSlot = (slotInfo) => {
    setSelectedSlot({
      start: slotInfo.start,
      end: slotInfo.end,
      resourceId: slotInfo.resourceId // Include the court that was clicked
    })
    setShowBookingForm(true)
  }

  // Handle event click (clicking on existing booking)
  const handleSelectEvent = (event) => {
    setSelectedEvent(event)
  }

  // Event style getter for custom styling
  const eventStyleGetter = (event) => {
    let backgroundColor
    let opacity = 0.9

    // Pending bookings show in light gray
    if (event.resource.status === 'pending') {
      backgroundColor = '#9ca3af' // light gray for pending
      opacity = 0.7
    } else {
      // Approved bookings show in court colors
      const courtColors = {
        'court_1_lower_deck': '#3b82f6', // blue
        'court_2_lower_creek': '#10b981', // green
        'court_3_upper_pavilion': '#f59e0b', // amber
        'court_4_upper_creek': '#8b5cf6'  // purple
      }
      backgroundColor = courtColors[event.resource.facility] || '#6b7280'
    }

    // Adjust styling based on view
    const baseStyle = {
      backgroundColor,
      borderRadius: '6px',
      opacity,
      color: 'white',
      border: '0px',
      display: 'block',
      fontWeight: '500'
    }

    // In month view, make events slightly smaller
    if (currentView === 'month') {
      return {
        style: {
          ...baseStyle,
          fontSize: '0.75rem',
          padding: '2px 4px'
        }
      }
    }

    // Default styling for day and week views
    return {
      style: {
        ...baseStyle,
        fontSize: '0.875rem'
      }
    }
  }

  const handleBookingSuccess = () => {
    setShowBookingForm(false)
    setSelectedSlot(null)
    fetchBookings() // Refresh bookings after successful submission
  }

  // Handle date navigation (in Eastern Time)
  const handleDateChange = (e) => {
    const newDate = moment.tz(e.target.value, TIMEZONE).toDate()
    setSelectedDate(newDate)
  }

  // Custom toolbar component
  const CustomToolbar = (toolbar) => {
    const goToBack = () => {
      toolbar.onNavigate('PREV')
    }

    const goToNext = () => {
      toolbar.onNavigate('NEXT')
    }

    const goToToday = () => {
      toolbar.onNavigate('TODAY')
    }

    const handleViewChange = (view) => {
      setCurrentView(view)
      toolbar.onView(view)
    }

    return (
      <div className="rbc-toolbar">
        <span className="rbc-btn-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="date"
            value={moment.tz(selectedDate, TIMEZONE).format('YYYY-MM-DD')}
            onChange={handleDateChange}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
            style={{ height: '32px' }}
          />
          <button type="button" onClick={goToToday} className="rbc-btn">
            Today
          </button>
          <button type="button" onClick={goToBack} className="rbc-btn">
            Previous
          </button>
          <button type="button" onClick={goToNext} className="rbc-btn">
            Next
          </button>
        </span>
        <span className="rbc-toolbar-label">{toolbar.label} ET</span>
        <span className="rbc-btn-group">
          <button
            type="button"
            onClick={() => handleViewChange('day')}
            className={`rbc-btn ${currentView === 'day' ? 'rbc-active' : ''}`}
          >
            Day
          </button>
          <button
            type="button"
            onClick={() => handleViewChange('week')}
            className={`rbc-btn ${currentView === 'week' ? 'rbc-active' : ''}`}
          >
            Week
          </button>
          <button
            type="button"
            onClick={() => handleViewChange('month')}
            className={`rbc-btn ${currentView === 'month' ? 'rbc-active' : ''}`}
          >
            Month
          </button>
        </span>
      </div>
    )
  }

  const tennisPrograms = [
    {
      title: "Men's Tennis",
      description: 'Competitive year-round ALTA teams for men of all skill levels',
      coordinator: 'Ken Ward',
      email: 'atlward@yahoo.com',
      icon: 'trophy'
    },
    {
      title: "Women's Tennis",
      description: 'Year-round ALTA teams for women players at various competitive levels',
      coordinator: 'Joyce Hanson',
      email: 'joycehanson05@comcast.net',
      icon: 'award'
    },
    {
      title: "Senior Women's Tennis",
      description: 'ALTA teams specifically designed for senior women players',
      coordinator: 'Joyce Hanson',
      email: 'joycehanson05@comcast.net',
      icon: 'users'
    },
    {
      title: 'Mixed Teams',
      description: 'Co-ed ALTA teams for doubles play throughout the year',
      icon: 'users'
    }
  ]

  const additionalOpportunities = [
    {
      name: 'USTA Teams',
      description: 'United States Tennis Association competitive teams and tournaments',
      link: 'https://www.usta.com'
    },
    {
      name: 'T2 Tennis',
      description: 'Flexible tennis leagues and social play opportunities',
      link: 'https://www.t2tennis.com'
    },
    {
      name: 'Ultimate Tennis',
      description: 'Modern tennis league with flexible scheduling',
      link: 'https://www.ultimatetennis.com'
    },
    {
      name: 'Round-Robin Tournaments',
      description: 'Special club tournaments on Memorial Day and Labor Day weekends'
    }
  ]

  const courtFeatures = [
    {
      title: '4 Lighted Courts',
      description: 'Play day or night on our professionally maintained tennis courts with lighting for evening matches',
      icon: 'trophy'
    },
    {
      title: 'Online Reservations',
      description: 'Easy online court reservation system - reserve your court below using our interactive calendar',
      icon: 'calendar'
    },
    {
      title: 'Daily Availability',
      description: 'Courts available every day for member use - perfect for practice, lessons, or casual play',
      icon: 'calendar'
    },
    {
      title: 'Professional Surface',
      description: 'Well-maintained court surfaces suitable for competitive and recreational play',
      icon: 'award'
    }
  ]

  const getIcon = (iconName) => {
    const icons = {
      trophy: Trophy,
      users: Users,
      calendar: CalendarIcon,
      award: Award,
      mail: Mail,
      externalLink: ExternalLink
    }
    const Icon = icons[iconName] || Trophy
    return <Icon className="w-6 h-6" />
  }

  const resources = [
    {
      name: 'ALTA',
      description: 'Atlanta Lawn Tennis Association',
      url: 'https://www.altatennis.org',
      logo: 'ALTA'
    },
    {
      name: 'USTA',
      description: 'United States Tennis Association',
      url: 'https://www.usta.com',
      logo: 'USTA'
    },
    {
      name: 'T2 Tennis',
      description: 'Flexible Tennis Leagues',
      url: 'https://www.t2tennis.com',
      logo: 'T2'
    },
    {
      name: 'Ultimate Tennis',
      description: 'Modern Tennis League',
      url: 'https://www.ultimatetennis.com',
      logo: 'UT'
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative text-white min-h-[450px] flex items-center"
        style={{backgroundImage: 'url(/images/tennis_1.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center'}}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-900/80 via-secondary-800/70 to-secondary-900/80"></div>
        <div className="container-custom relative z-10 py-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 animate-fade-in text-4xl md:text-5xl lg:text-6xl font-bold">
              Tennis at Lockridge Forest
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-95">
              Four professionally maintained lighted tennis courts hosting competitive ALTA, USTA,
              and T2 teams year-round
            </p>
          </div>
        </div>
      </section>

      {/* Court Features */}
      <section className="py-10 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-gray-900">Court Facilities</h2>
            <p className="text-lg text-gray-600">
              Everything you need for an exceptional tennis experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courtFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-secondary-500 to-secondary-500 text-white mb-4">
                  {getIcon(feature.icon)}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{feature.description}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Court Reservation Calendar */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-gray-900">Reserve a Court</h2>
            <p className="text-lg text-gray-600">
              Click on any available time slot to reserve a tennis court
            </p>
          </div>

          {/* Information Banner */}
          <div className="mb-8 bg-secondary-50 border-l-4 border-secondary-600 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">
              How to Reserve a Court
            </h3>
            <ul className="text-secondary-800 space-y-2">
              <li className="flex items-start">
                <span className="font-bold mr-2">1.</span>
                <span>Switch between Day, Week, and Month views using the buttons in the top right of the calendar</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">2.</span>
                <span>Click on any available date/time on the calendar below</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">3.</span>
                <span>Select your court, enter your name and reason for reservation</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">4.</span>
                <span>Choose your start time (default is 1 hour, adjustable in 15-minute increments)</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">5.</span>
                <span>Submit - members' reservations are instantly confirmed and appear on the calendar!</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">Note:</span>
                <span>Members can have up to 2 court reservations within a 7-day period and book up to 2 weeks in advance. Tennis captains and board members may book the courts necessary for practice and matches and further in advance.</span>
              </li>
            </ul>
            <div className="mt-4 pt-4 border-t border-secondary-200 space-y-2">
              <p className="text-secondary-800 text-sm font-medium">
                <span className="inline-block w-3 h-3 bg-gray-400 rounded opacity-70 mr-2 align-middle"></span>
                Gray events are pending admin approval (non-member bookings require verification)
              </p>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 rounded mr-1.5" style={{ backgroundColor: '#3b82f6' }}></span>
                  Court 1 (Blue)
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 rounded mr-1.5" style={{ backgroundColor: '#10b981' }}></span>
                  Court 2 (Green)
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 rounded mr-1.5" style={{ backgroundColor: '#f59e0b' }}></span>
                  Court 3 (Amber)
                </span>
                <span className="flex items-center">
                  <span className="inline-block w-3 h-3 rounded mr-1.5" style={{ backgroundColor: '#8b5cf6' }}></span>
                  Court 4 (Purple)
                </span>
              </div>
            </div>
          </div>

          {/* Calendar Controls */}
          <div className="mb-6 flex flex-wrap items-center justify-end gap-4">
            <button
              onClick={() => setShowBookingForm(true)}
              className="btn btn-primary"
            >
              New Reservation
            </button>
          </div>

          {/* Calendar Card */}
          <div className="card p-6 mb-8">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-red-600 text-lg">{error}</p>
                <button
                  onClick={fetchBookings}
                  className="btn btn-primary mt-4"
                >
                  Retry
                </button>
              </div>
            ) : (
              <Calendar
                localizer={localizer}
                events={filteredEvents}
                resources={currentView === 'day' ? calendarResources : undefined}
                resourceIdAccessor={currentView === 'day' ? "resourceId" : undefined}
                resourceTitleAccessor={currentView === 'day' ? "resourceTitle" : undefined}
                startAccessor="start"
                endAccessor="end"
                date={selectedDate}
                onNavigate={(newDate) => setSelectedDate(newDate)}
                style={{
                  height: currentView === 'week' ?
                    (window.innerWidth < 768 ? 700 : 900) :
                    (window.innerWidth < 768 ? 600 : 800)
                }}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                selectable
                eventPropGetter={eventStyleGetter}
                views={['day', 'week', 'month']}
                view={currentView}
                onView={(view) => setCurrentView(view)}
                step={15}
                timeslots={4}
                min={new Date(2024, 0, 1, 6, 0, 0)}
                max={new Date(2024, 0, 1, 23, 59, 59)}
                popup
                tooltipAccessor={(event) => event.title}
                components={{
                  toolbar: CustomToolbar
                }}
                messages={{
                  next: 'Next',
                  previous: 'Previous',
                  today: 'Today',
                  day: 'Day',
                  week: 'Week',
                  month: 'Month',
                  showMore: (total) => `+${total} more`
                }}
              />
            )}
          </div>
        </div>
      </section>

      {/* Tennis Court Rules */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-3 text-gray-900">Tennis Court Rules</h2>
            <p className="text-lg text-gray-600">
              Please follow these rules to ensure an enjoyable experience for all members.
              Wait until play has stopped before crossing behind a court or retrieving a ball from an adjacent court.
            </p>
          </div>

          <div className="max-w-3xl mx-auto bg-secondary-50 border border-secondary-200 rounded-lg p-6">
            <div className="flex items-center mb-6">
              <BookOpen className="w-6 h-6 text-secondary-700 mr-2 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-secondary-900">Official Court Rules</h3>
            </div>

            {/* 1. General */}
            <div className="mb-6">
              <h4 className="text-base font-bold text-secondary-900 mb-3">1. General</h4>
              <ol className="space-y-2 text-secondary-800 list-none pl-0">
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">a.</span>
                  <span>Courts are for racquet sports only (tennis, pickleball where permitted).</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">b.</span>
                  <span>Only players and spectators keeping voices at a reasonable level are allowed on or near courts during play.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">c.</span>
                  <span>Proper tennis attire and non-marking court shoes are required on all courts.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">d.</span>
                  <span>No glass containers or smoking on or near the courts.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">e.</span>
                  <span>Remove leaves and debris from the court before play. Use the roller squeegee for wet courts if available.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">f.</span>
                  <span>Do not move benches or scoreboards from their assigned locations.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">g.</span>
                  <span>Please clean up after yourself. Remove all balls, trash, and personal items when leaving the court.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">h.</span>
                  <span>Lock the gates when courts are not in use.</span>
                </li>
              </ol>
            </div>

            {/* 2. Usage */}
            <div className="mb-6">
              <h4 className="text-base font-bold text-secondary-900 mb-3">2. Usage</h4>
              <ol className="space-y-2 text-secondary-800 list-none pl-0">
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">a.</span>
                  <span>Courts are available 8:00 AM - 10:00 PM Sunday through Thursday, and 8:00 AM - 11:00 PM Friday and Saturday.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">b.</span>
                  <span>Gates should be locked when courts are not in use.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">c.</span>
                  <span>Each member household may reserve one court at a time. Guest rules:</span>
                </li>
                <li className="pl-7 space-y-1 text-sm">
                  <p className="flex items-start"><span className="text-secondary-500 mr-2">i.</span>Maximum of 3 guests per court per session.</p>
                  <p className="flex items-start"><span className="text-secondary-500 mr-2">ii.</span>A member must be present when guests are playing.</p>
                  <p className="flex items-start"><span className="text-secondary-500 mr-2">iii.</span>Guests may not play without a member present.</p>
                  <p className="flex items-start"><span className="text-secondary-500 mr-2">iv.</span>Members are responsible for the conduct of their guests.</p>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">d.</span>
                  <span>Adults have priority over juniors on weekdays from 9:00 AM - 3:00 PM and after 6:00 PM.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">e.</span>
                  <span>Members under 16 must have a parent or guardian present. Members 16-17 may reserve and play without supervision.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">f.</span>
                  <span>Solo practice on a court is not allowed when others are waiting to play.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">g.</span>
                  <span>Ball machines are permitted when courts are available but must be removed promptly when others are waiting.</span>
                </li>
              </ol>
            </div>

            {/* 3. Reservations */}
            <div>
              <h4 className="text-base font-bold text-secondary-900 mb-3">3. Reservations</h4>
              <ol className="space-y-2 text-secondary-800 list-none pl-0">
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">a.</span>
                  <span>Maximum court usage is 1.5 hours per session when others are waiting. If no one is waiting, play may continue.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">b.</span>
                  <span>Priority order: (1) Scheduled team matches, (2) Scheduled team practices, (3) Member reservations, (4) Walk-on play.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">c.</span>
                  <span>Team captains/chairs should coordinate schedules to minimize conflicts between teams and open play.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">d.</span>
                  <span>All reservations should be made through the club website reservation system above.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">e.</span>
                  <span>Team practices are limited to a maximum of 2 courts at a time.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">f.</span>
                  <span>Home team matches may reserve a maximum of 3 courts.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">g.</span>
                  <span>During playoffs, the team may reserve additional courts as necessary.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">h.</span>
                  <span>Matches extending beyond their reserved time have priority to finish, but players should be mindful of the next reservation.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">i.</span>
                  <span>Members are limited to 2 reservations within any rolling 7-day period. Captains and board members may book courts as needed for team activities.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">j.</span>
                  <span>If a party does not show within 20 minutes of their reserved time, the court is forfeited and available for others.</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold text-secondary-600 mr-2 flex-shrink-0 w-5">k.</span>
                  <span>Unreserved courts are available on a first-come, first-served basis.</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Tennis Programs */}
      <section className="py-10 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-gray-900">ALTA Tennis Programs</h2>
            <p className="text-lg text-gray-600">
              Year-round competitive tennis opportunities for all skill levels
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {tennisPrograms.map((program, index) => {
              const Icon = program.icon === 'trophy' ? Trophy : program.icon === 'award' ? Award : Users
              return (
                <div key={index} className="card p-6 hover:shadow-xl transition-shadow">
                  <div className="flex flex-col items-center text-center mb-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-secondary-500 to-secondary-500 text-white flex items-center justify-center mb-3">
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-gray-900">{program.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{program.description}</p>
                    {program.coordinator && (
                      <div className="bg-gray-50 rounded-lg p-4 w-full">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Coordinator</p>
                        <p className="text-sm text-gray-900 mb-1">{program.coordinator}</p>
                        <a
                          href={`mailto:${program.email}`}
                          className="text-xs text-primary-600 hover:text-primary-800 break-all inline-flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" />
                          {program.email}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Tennis Court Photos */}
      <section className="py-10 bg-gradient-to-br from-gray-50 to-secondary-50">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-gray-900">Our Tennis Facilities</h2>
            <p className="text-lg text-gray-600">
              Professional courts in a beautiful setting
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img src="/images/tennis_2.jpeg" alt="Tennis Courts" className="w-full h-64 object-cover" />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img src="/images/tennis_3.jpeg" alt="Tennis Court View" className="w-full h-64 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Additional Opportunities */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-gray-900">Additional Tennis Opportunities</h2>
            <p className="text-lg text-gray-600">
              More ways to play and compete throughout the year
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {additionalOpportunities.map((opportunity, index) => (
              <div key={index} className="card p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <Trophy className="w-5 h-5 text-secondary-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{opportunity.name}</h3>
                    <p className="text-gray-600 text-sm mb-2">{opportunity.description}</p>
                    {opportunity.link && (
                      <a
                        href={opportunity.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:text-primary-800 inline-flex items-center gap-1"
                      >
                        Visit website
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Helpful Resources */}
      <section className="py-10 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-gray-900">Helpful Tennis Resources</h2>
            <p className="text-lg text-gray-600">
              Links to tennis organizations and leagues
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {resources.map((resource, index) => (
              <a
                key={index}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="card p-6 text-center hover:scale-105 transition-transform group"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-secondary-500 to-secondary-500 text-white flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {resource.logo}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{resource.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{resource.description}</p>
                <span className="text-sm text-primary-600 group-hover:text-primary-800 inline-flex items-center gap-1">
                  Visit site
                  <ExternalLink className="w-4 h-4" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Coordinators */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto card p-8 text-center">
            <h2 className="mb-6 text-gray-900">Get Involved in Tennis</h2>
            <p className="text-lg text-gray-600 mb-8">
              Interested in joining one of our tennis teams? Contact our coordinators to learn more
              about upcoming seasons and team opportunities.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-center mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary-500 to-secondary-500 text-white flex items-center justify-center">
                    <Trophy className="w-6 h-6" />
                  </div>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Men's Tennis</h3>
                <p className="text-gray-700 mb-2">Ken Ward</p>
                <a
                  href="mailto:atlward@yahoo.com"
                  className="text-primary-600 hover:text-primary-800 text-sm inline-flex items-center gap-1"
                >
                  <Mail className="w-4 h-4" />
                  atlward@yahoo.com
                </a>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-center mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary-500 to-secondary-500 text-white flex items-center justify-center">
                    <Award className="w-6 h-6" />
                  </div>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Women's & Senior Women's</h3>
                <p className="text-gray-700 mb-2">Joyce Hanson</p>
                <a
                  href="mailto:joycehanson05@comcast.net"
                  className="text-primary-600 hover:text-primary-800 text-sm inline-flex items-center gap-1"
                >
                  <Mail className="w-4 h-4" />
                  joycehanson05@comcast.net
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-10 bg-gradient-to-r from-secondary-600 to-secondary-600 text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6">Ready to Serve?</h2>
            <p className="text-xl mb-8 opacity-95">
              Join Lockridge Forest and gain access to our premium tennis facilities and
              competitive teams. Whether you're a beginner or seasoned player, there's a place for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/membership" className="btn btn-primary bg-white text-secondary-700 hover:bg-gray-100">
                View Membership Options
              </Link>
              <Link to="/" className="btn btn-outline border-white text-white hover:bg-white hover:text-secondary-700">
                Explore More Facilities
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">Reservation Details</h3>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500 w-24">Court</span>
                <span className="text-gray-900 font-medium">
                  {selectedEvent.resource.courtLabel || courts.find(c => c.value === selectedEvent.resource.facility)?.label || selectedEvent.resource.facility}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500 w-24">Reserved by</span>
                <span className="text-gray-900">{abbreviateName(selectedEvent.resource.contactName)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500 w-24">Time</span>
                <span className="text-gray-900">
                  {moment.tz(selectedEvent.start, TIMEZONE).format('MMM D, YYYY h:mm A')} - {moment.tz(selectedEvent.end, TIMEZONE).format('h:mm A')} ET
                </span>
              </div>
              {selectedEvent.resource.eventType && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-500 w-24">Reason</span>
                  <span className="text-gray-900">{selectedEvent.resource.eventType}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500 w-24">Status</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  selectedEvent.resource.status === 'approved'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedEvent.resource.status === 'approved' ? 'Approved' : 'Pending Approval'}
                </span>
              </div>
            </div>
            <div className="p-5 border-t bg-gray-50 rounded-b-xl flex items-center justify-between">
              <Link
                to="/manage-booking"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={() => setSelectedEvent(null)}
              >
                Manage a Reservation
              </Link>
              <button
                onClick={() => setSelectedEvent(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Court Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">
                Reserve a Tennis Court
              </h2>
              <button
                onClick={() => {
                  setShowBookingForm(false)
                  setSelectedSlot(null)
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <CourtBookingForm
                initialSlot={selectedSlot}
                onSuccess={handleBookingSuccess}
                onCancel={() => {
                  setShowBookingForm(false)
                  setSelectedSlot(null)
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Tennis
